/**
 * fishing_water.ts — the water a cast may be offered at, judged as the SERVER will judge it (2026-09-28, "fix the
 * fishing to work better"). castLine now checks the water itself (server lib/water_index + water_rules); the client
 * asks the SAME rule (shared/water_rules.ts, byte-identical to the server's) on the SAME inputs — the f32 coordinates
 * the wire carries, the river-free ground the server sees (terrainHeightNoRivers), the baked lakes in the server
 * table's rounding (lakes.ts bakedLakeRows), the plateaus kept dry — so the prompt never offers a cast the server
 * refuses. Rivers are the rendered data: the server accepts a river cast within a channel's half width + 4 m, wider
 * than any rendered ribbon.
 */
import { lakeWater, seaWater, type Water } from '../../../shared/water_rules.js';
import { terrainHeightNoRivers, plateauLevelAt } from '../../../shared/terrain.js';
import { commonsSeaLevel } from './sea_level.js';
import { bakedLakeRows } from './lakes.js';

/** The water at (x, z) a cast may claim, given what is RENDERED there (`level`: the rendered still-water surface, the
 *  pond's level the cast carries). `seed` '' = not the Commons (no rule to mirror: the server keeps its old trust
 *  outside the Commons, the rendered water stands). */
export function fishingWaterAt(seed: string, x: number, z: number, rendered: Water | null, level: number | null = null): Water | null {
  if (rendered === 'river') return 'river';
  if (!seed) return rendered;
  const fx = Math.fround(x), fz = Math.fround(z); // what castLine's f32 arguments carry
  const H = (a: number, b: number): number => terrainHeightNoRivers(seed, a, b);
  const sea = commonsSeaLevel(seed);
  if (rendered === 'lake') return lakeWater(bakedLakeRows(seed), seed, H, sea, fx, fz, (a, b) => plateauLevelAt(seed, a, b) !== null, castLevel(level)) ? 'lake' : null;
  // The sea (rendered or not yet rendered: the shoreline bake lags a teleport) by the server's own freeboard rule.
  return seaWater(H, sea, fx, fz) ? 'sea' : null;
}

/** The level as castLine carries it: f32, and 0 / not finite = none (the server reads it the same way). */
export function castLevel(level: number | null): number {
  if (level === null || !Number.isFinite(level) || level === 0) return NaN;
  const f = Math.fround(level);
  return f === 0 ? NaN : f;
}
